import resolveImage from "./resolveImage";
import resolveManual from "./resolveManual";
import {
  debugLog,
  canonCamelCase,
  isPreview,
  getCkHtml,
  applyVariables,
} from "../../utils/index";

const resolveCallout = ({ calloutType, calloutText }, variableValues = {}) => {
  const html = getCkHtml(calloutText);
  if (calloutType && html !== undefined) {
    return {
      text: applyVariables(html, variableValues, isPreview()),
      type: calloutType,
    };
  }
  return null;
};

/**
 * Helper to ensure we either use the existing uid
 * or build one if not present.
 */
const ensureUid = (obj, prefix, index) => {
  if (!obj) return null;
  // If there's already a uid, use it. Otherwise construct one from prefix, id, and index
  return obj.uid ? obj.uid : `${prefix}_${obj.id}_${index}`;
};

const resolveContentBlocks = (blocks, variableValues) => {
  if (!blocks || blocks.length === 0) return [];
  return blocks.map((block, blockIdx) =>
    // eslint-disable-next-line no-use-before-define
    resolveContentBlock({ ...block, variableValues }, blockIdx)
  );
};

const resolveContentBlock = (block, blockIdx) => {
  if (!block) return null;
  const {
    id: rawId,
    uid,
    typeHandle,
    __typename,
    image,
    columnBlockComposableEntries,
    variableValues = {},
    ...rest
  } = block;
  const id = Number(rawId);
  /* ---------------------------------------------------------------
   * 1) Pick up the *new* richText field (CKEditor) if present
   * 2) Fallback to legacy “paragraph” field
   * 3) Process exactly once → normalised into rest.paragraph
   * ------------------------------------------------------------- */
  const rawParagraphSource =
    rest.paragraph !== undefined ? rest.paragraph : rest.richText; // richText → rawHtml

  if (rawParagraphSource !== undefined) {
    const raw = getCkHtml(rawParagraphSource); // unwrap { rawHtml } | string
    if (raw !== undefined) {
      rest.paragraph = applyVariables(raw, variableValues, {
        wrap: isPreview(), // keep tooltip spans in preview
      });
    }
    delete rest.richText; // avoid leaking the extra prop
  }
  // If the block is a columnComposableBlock, we recursively resolve children
  let nestedColumns = [];
  if (typeHandle === "columnComposableBlock" && columnBlockComposableEntries) {
    nestedColumns = resolveContentBlocks(
      columnBlockComposableEntries,
      variableValues
    );
  }
  const canonicalType = canonCamelCase(typeHandle || __typename);
  return {
    id,
    uid: ensureUid(block, "contentBlock", blockIdx), // If already has uid, use it
    type: canonicalType,
    content: {
      uid: ensureUid({ ...block, id: `content_${id}` }, "subContent", blockIdx),
      ...rest,
      image: image ? resolveImage(image[0]) : undefined,
      callout: resolveCallout(rest, variableValues),
      columnBlockComposableEntries: nestedColumns,
    },
    variableValues,
  };
};

/**
 * Resolve a single entry, optionally passing in an index
 * so that we can ensure unique UIDs at this level.
 */
const resolveEntry = (entry, entryIdx = 0, variableValues = {}) => {
  if (!entry) return null;
  if (entry && entry._resolved) {
    return entry;
  }

  const {
    id: rawId,
    userTitle,
    subtopicGroupTag,
    topicArticleContentBuilder,
    topicArticleContentBuilderComposableEntries,
    topicArticleStepBuilder,
    stepArticlesComposableEntries,
    step,
    typeHandle,
    topicArticles = [],
    shallow = false,
    __typename,
  } = entry;
  // Convert id to a number if needed
  const id = Number(rawId);
  const type = canonCamelCase(typeHandle || __typename);
  const appliedUserTitle = applyVariables(userTitle, variableValues);
  const appliedHtmlUserTitle = isPreview()
    ? applyVariables(userTitle, variableValues, {
        wrap: true,
      })
    : null;

  // For clarity we define the UID for this entry:
  const entryUid = ensureUid(entry, "entry", entryIdx);

  if (["product"].includes(type)) {
    return resolveManual(entry, [], variableValues);
  }
  if (["chapter"].includes(type)) {
    return {
      id: Number(entry.id),
      uid: entryUid,
      title: appliedUserTitle,
      userTitle: appliedUserTitle,
      htmlTitle: appliedHtmlUserTitle,
      type,
      topics: topicArticles.map
        ? topicArticles.map((t, i) => resolveEntry(t, i, variableValues))
        : [],
      _resolved: true,
    };
  }
  if (["glossaryTerm"].includes(type)) {
    /* unwrap CKEditor → HTML, then apply variables (so tooltips still work) */
    /* 1 | unwrap definition
     *    • new format:  { rawHtml: "<p>…</p>" }
     *    • old format:  "<p>…</p>" (plain string)                        */
    const raw =
      typeof entry.definition === "string"
        ? entry.definition
        : entry.definition?.rawHtml ?? getCkHtml(entry.definition);
    const htmlDef = raw
      ? applyVariables(raw, variableValues, { wrap: isPreview() })
      : "";

    return {
      id,
      uid: entryUid,
      type,
      title: applyVariables(entry.title, variableValues), // plain title
      definition: htmlDef, // processed HTML
      _resolved: true,
    };
  }

  let content = null;
  let steps = null;

  // Grab the group's title from the first item in subtopicGroupTag (if it exists and has a value)
  let group = null;
  if (subtopicGroupTag?.length && subtopicGroupTag[0].title) {
    group = applyVariables(subtopicGroupTag[0].title, variableValues);
  }

  if (topicArticleContentBuilder) {
    // articleLongform_Entry
    content = resolveContentBlocks(topicArticleContentBuilder, variableValues);
  } else if (
    topicArticleContentBuilderComposableEntries ||
    ["longformComposableEntries"].includes[type]
  ) {
    // articleLongformComposableEntries_Entry
    //
    // Here we map over child entries, passing each childEntry plus its index:
    content = topicArticleContentBuilderComposableEntries.map(
      (childEntry, idx) => resolveEntry(childEntry, idx, variableValues)
    );
  } else if (topicArticleStepBuilder) {
    // articleWithSteps_Entry
    steps = topicArticleStepBuilder.map((stepBlock, idx) => {
      const stepContentBlocks = stepBlock.step; // This is an array of content blocks
      const resolvedContent = resolveContentBlocks(
        stepContentBlocks,
        variableValues
      );

      return {
        id: Number(stepBlock.id),
        uid: ensureUid(stepBlock, "step", idx), // Use or build a UID for each step
        type,
        title: null, // No title available in this data structure
        content: resolvedContent,
        variableValues,
      };
    });
  } else if (
    stepArticlesComposableEntries ||
    ["articleStepByStepComposableEntries"].includes[type]
  ) {
    // articleStepByStepComposableEntries_Entry
    //
    // Also pass index so each child can get a unique UID
    steps = stepArticlesComposableEntries.map((childEntry, idx) =>
      resolveEntry(childEntry, idx, variableValues)
    );
  } else if (step) {
    // topicArticleStepBuilderBlock_Entry
    content = resolveContentBlocks(
      step.topicArticleContentBuilder,
      variableValues
    );
  } else if (
    [
      "textBlock",
      "textPlusImageBlock",
      "calloutBlock",
      "largeImageBlock",
      "videoBlock",
      "textBlock2Column",
      "textBlock3Column",
      "columnComposableBlock",
      "fullWidthImageBlock",
      "richTextInlineImagesBlock",
    ].includes(type)
  ) {
    // Content blocks
    const contentBlock = resolveContentBlock({ ...entry, variableValues }, 0);
    content = [contentBlock]; // Ensure content is an array
  } else if (
    shallow &&
    [
      "articleWithSteps",
      "articleLongform",
      "articleLongformComposableEntries",
      "articleStepByStepComposableEntries",
    ].includes(type)
  ) {
    debugLog(
      `Shallow article entry type ${type}, is okay to not have nested items.`
    );
  } else {
    debugLog(`Entry id ${id} is unhandled type: ${type}`);
  }

  return {
    id,
    uid: entryUid, // Ensure top-level entry has a UID
    title: appliedUserTitle,
    htmlTitle: appliedHtmlUserTitle,
    type,
    group,
    content,
    steps,
    _resolved: true,
  };
};

export default resolveEntry;
